/**
 * Estimate Provider (last resort)
 * Generates a heuristic-based wait time estimate from time-of-day / day-of-week patterns.
 * No external API calls — fully offline.
 */
import { WaitTimeProvider, ProviderResponse } from '../types';

/** Base wait times by airport tier */
const AIRPORT_TIERS: Record<string, 'major' | 'large' | 'medium'> = {
  ATL: 'major', LAX: 'major', ORD: 'major', DFW: 'major', DEN: 'major',
  JFK: 'major', SFO: 'major', SEA: 'large', LAS: 'large', MCO: 'large',
  EWR: 'major', CLT: 'large', PHX: 'large', IAH: 'large', MIA: 'major',
  BOS: 'large', MSP: 'large', DTW: 'large', PHL: 'large', LGA: 'major',
  SAT: 'medium', SLC: 'large', FLL: 'large', BWI: 'large', DCA: 'large',
  IAD: 'large', MDW: 'large', SAN: 'large', TPA: 'large', PDX: 'large',
};

const BASE_WAIT: Record<string, number> = {
  major: 18,
  large: 12,
  medium: 8,
};

/** Hour-of-day multipliers (0-23) */
const HOUR_MULTIPLIERS: number[] = [
  0.5, 0.4, 0.3, 0.3, 0.5, 1.3, 1.6, 1.8, // 0-7
  1.4, 1.0, 0.8, 0.9, 1.0, 1.0, 1.1, 1.3,  // 8-15
  1.5, 1.6, 1.4, 1.0, 0.8, 0.7, 0.6, 0.5,  // 16-23
];

/** Day-of-week multipliers (0=Sun through 6=Sat) */
const DAY_MULTIPLIERS = [1.3, 1.2, 0.9, 0.9, 1.0, 1.3, 1.1];

export const estimateProvider: WaitTimeProvider = {
  name: 'estimate',

  async fetch(airportCode: string): Promise<ProviderResponse | null> {
    const now = new Date();
    const hour = now.getHours();
    const day = now.getDay();

    const tier = AIRPORT_TIERS[airportCode] || 'medium';
    const base = BASE_WAIT[tier];
    const hourMul = HOUR_MULTIPLIERS[hour];
    const dayMul = DAY_MULTIPLIERS[day];

    const estimated = Math.round(base * hourMul * dayMul);
    // Add slight jitter (±2 min) so it doesn't look static
    const jitter = Math.round((Math.random() - 0.5) * 4);
    const finalWait = Math.max(2, estimated + jitter);

    return {
      waitTimeMinutes: finalWait,
      lastUpdatedISO: now.toISOString(),
      source: 'estimate',
      confidence: 0.3,
      disclaimer: 'Estimated based on typical patterns for this time and day',
    };
  },
};
